/**
 * Importação manual de mídia (PLAN §23, F4-T04, F4-T05).
 *
 * Existe porque a fonte CCB só expõe vídeos no YouTube: sem uma origem autorizada de
 * MP4, este é o único caminho para ter o glossário completo offline. O usuário escolhe
 * um arquivo que já tem e o associa a um sinal.
 *
 * A escrita é transacional na ordem certa: valida → grava o arquivo → só então marca
 * o sinal como disponível. Inverter isso deixaria o banco afirmando que existe um
 * arquivo que não existe.
 */
import { updateState } from '@/db/repositories/localSignState';
import type { LocalSignState } from '@/types/catalog';
import {
  canDecodeVideo,
  sha256Hex,
  validateVideoBlob,
  type ValidationIssue,
} from '@/services/media/validation';

/** Escrita no armazenamento de arquivos. Implementado sobre OPFS na FASE 10. */
export interface LocalMediaWriter {
  write(signId: string, blob: Blob): Promise<void>;
  remove(signId: string): Promise<void>;
  has(signId: string): Promise<boolean>;
}

export interface ImportResult {
  ok: boolean;
  signId: string;
  bytes?: number;
  checksum?: string;
  issues: ValidationIssue[];
}

export interface ImportOptions {
  /** Verificar se o navegador consegue decodificar. Custa mais, mas prova que toca. */
  verifyDecodable?: boolean;
  /** Substituir um arquivo já existente para este sinal. */
  replace?: boolean;
}

export async function importManualMedia(
  signId: string,
  file: File | Blob,
  writer: LocalMediaWriter,
  options: ImportOptions = {},
): Promise<ImportResult> {
  const { verifyDecodable = true, replace = false } = options;
  const fileName = file instanceof File ? file.name : undefined;

  if (!replace && (await writer.has(signId))) {
    return {
      ok: false,
      signId,
      issues: [
        {
          code: 'not-decodable',
          message: 'Já existe um arquivo para este sinal. Use a opção de substituir.',
        },
      ],
    };
  }

  const validation = await validateVideoBlob(file, {
    ...(fileName ? { fileName } : {}),
  });
  if (!validation.valid) {
    return { ok: false, signId, issues: validation.issues };
  }

  if (verifyDecodable && !(await canDecodeVideo(file))) {
    return {
      ok: false,
      signId,
      issues: [
        {
          code: 'not-decodable',
          message: 'O navegador não conseguiu ler os metadados deste vídeo.',
        },
      ],
    };
  }

  const checksum = await sha256Hex(file);

  try {
    await writer.write(signId, file);
  } catch (error) {
    await markImportFailed(signId, error);
    return {
      ok: false,
      signId,
      issues: [
        {
          code: 'not-decodable',
          message: `Não foi possível gravar o arquivo: ${(error as Error).message}`,
        },
      ],
    };
  }

  // Só agora o banco passa a afirmar que o sinal está disponível offline.
  await updateState(signId, (current) => ({
    ...current,
    offline: {
      status: 'available',
      totalBytes: file.size,
      bytesDownloaded: file.size,
      localFileName: `${signId}.mp4`,
      checksum,
      sourceProvider: 'manual',
      downloadedAt: new Date().toISOString(),
    },
  }));

  return { ok: true, signId, bytes: file.size, checksum, issues: [] };
}

async function markImportFailed(signId: string, error: unknown): Promise<void> {
  await updateState(signId, (current) => ({
    ...current,
    offline: {
      ...current.offline,
      status: 'error',
      error: error instanceof Error ? error.message : String(error),
    },
  }));
}

/**
 * Remove a mídia local de um sinal.
 *
 * Apaga o arquivo e zera **apenas** o bloco `offline` — favorito, progresso e
 * histórico continuam intactos (PLAN F13-T14).
 */
export async function removeLocalMedia(
  signId: string,
  writer: LocalMediaWriter,
): Promise<void> {
  await writer.remove(signId);
  await updateState(signId, (current): LocalSignState => ({
    ...current,
    offline: { status: 'none' },
  }));
}
